import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  testWhmcsConnection,
  listWhmcsClients,
  listWhmcsInvoices,
  listWhmcsProducts,
  listWhmcsOrders,
  listWhmcsLogs,
  createWhmcsInvoice,
  captureWhmcsPayment,
  getWhmcsSettings,
  saveWhmcsSettings,
} from "@/lib/whmcs.functions";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw, PlugZap, CheckCircle2, XCircle, Save, Power, AlertTriangle, Users, FileText, Package, ShoppingCart } from "lucide-react";
import { StatsStrip } from "@/components/admin/stats-strip";

export const Route = createFileRoute("/_authenticated/admin/whmcs")({
  component: WhmcsPage,
});

function WhmcsPage() {
  const testFn = useServerFn(testWhmcsConnection);
  const getFn = useServerFn(getWhmcsSettings);
  const settingsQ = useQuery({ queryKey: ["whmcs-settings"], queryFn: () => getFn({}) });

  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string; totalResults?: number } | null>(null);
  const testMut = useMutation({
    mutationFn: () => testFn({}),
    onSuccess: (r) => {
      setTestResult(r);
      if (r.ok) toast.success(`WHMCS connection OK — ${r.totalResults} log entries`);
      else toast.error(r.error ?? "Connection failed");
    },
    onError: (e: any) => { setTestResult({ ok: false, error: e?.message }); toast.error(e?.message); },
  });

  const s = (settingsQ.data?.settings as any) ?? {};
  const configured = Boolean(s.api_url && s.api_identifier && s.api_secret);
  const enabled = s.enabled ?? true;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 sm:flex sm:flex-wrap sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">WHMCS</h2>
          <p className="text-sm text-muted-foreground">
            Connect to your existing WHMCS install and manage billing core: clients, invoices, products and orders.
          </p>
        </div>
        <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending || !configured}>
          {testMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
          Test connection
        </Button>
      </div>

      <StatsStrip
        tiles={[
          {
            label: "Status",
            value: !configured ? "Not set" : enabled ? "Enabled" : "Disabled",
            icon: Power,
            tone: !configured ? "warning" : enabled ? "success" : "danger",
          },
          {
            label: "Last test",
            value: testResult ? (testResult.ok ? "Passed" : "Failed") : "—",
            icon: testResult?.ok ? CheckCircle2 : XCircle,
            tone: testResult ? (testResult.ok ? "success" : "danger") : "default",
            hint: testResult?.ok ? `${testResult.totalResults} log entries` : (testResult?.error ?? "Run a test"),
          },
          { label: "Clients", value: "—", icon: Users, hint: "Live from WHMCS" },
          { label: "Invoices", value: "—", icon: FileText, hint: "Live from WHMCS" },
          { label: "Products", value: "—", icon: Package, hint: "Live from WHMCS" },
        ]}
      />

      <Tabs defaultValue="connection">
        <PillTabsList search className="flex flex-wrap">
          <PillTabsTrigger value="connection">Connection</PillTabsTrigger>
          <PillTabsTrigger value="clients"><Users className="mr-1.5 h-3.5 w-3.5" />Clients</PillTabsTrigger>
          <PillTabsTrigger value="invoices"><FileText className="mr-1.5 h-3.5 w-3.5" />Invoices</PillTabsTrigger>
          <PillTabsTrigger value="products"><Package className="mr-1.5 h-3.5 w-3.5" />Products</PillTabsTrigger>
          <PillTabsTrigger value="orders"><ShoppingCart className="mr-1.5 h-3.5 w-3.5" />Orders</PillTabsTrigger>
          <PillTabsTrigger value="logs">API logs</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="connection" className="pt-4"><ConnectionTab /></TabsContent>
        <TabsContent value="clients" className="pt-4"><ClientsTab /></TabsContent>
        <TabsContent value="invoices" className="pt-4"><InvoicesTab /></TabsContent>
        <TabsContent value="products" className="pt-4"><ProductsTab /></TabsContent>
        <TabsContent value="orders" className="pt-4"><OrdersTab /></TabsContent>
        <TabsContent value="logs" className="pt-4"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Connection ----------------------------------------------------------
function ConnectionTab() {
  const testFn = useServerFn(testWhmcsConnection);
  const getFn = useServerFn(getWhmcsSettings);
  const saveFn = useServerFn(saveWhmcsSettings);
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ["whmcs-settings"],
    queryFn: () => getFn({}),
  });

  const [form, setForm] = useState({
    api_url: "",
    api_identifier: "",
    api_secret: "",
    access_key: "",
    enabled: true,
  });
  const [hydrated, setHydrated] = useState(false);

  if (settingsQ.data && !hydrated) {
    const s = settingsQ.data.settings as any;
    setForm({
      api_url: s.api_url ?? "",
      api_identifier: s.api_identifier ?? "",
      api_secret: s.api_secret ?? "",
      access_key: s.access_key ?? "",
      enabled: s.enabled ?? true,
    });
    setHydrated(true);
  }

  const [result, setResult] = useState<{ ok: boolean; error?: string; totalResults?: number } | null>(null);
  const testMut = useMutation({
    mutationFn: () => testFn({}),
    onSuccess: (r) => {
      setResult(r);
      if (r.ok) toast.success("WHMCS connection OK");
      else toast.error(r.error ?? "Connection failed");
    },
    onError: (e: any) => { setResult({ ok: false, error: e?.message }); toast.error(e?.message); },
  });

  const saveMut = useMutation({
    mutationFn: (patch: Partial<typeof form>) =>
      saveFn({ data: patch }),
    onSuccess: () => {
      toast.success("WHMCS settings saved");
      qc.invalidateQueries({ queryKey: ["whmcs-settings"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
  });

  const toggleEnabled = (next: boolean) => {
    setForm((f) => ({ ...f, enabled: next }));
    saveMut.mutate({ enabled: next });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Power className="h-4 w-4" /> Integration status</CardTitle>
          <CardDescription>
            When disabled, WHMCS-backed features (billing, clients, invoices, orders) are turned off for all users.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium">{form.enabled ? "Enabled" : "Disabled"}</div>
            <div className="text-xs text-muted-foreground">
              {form.enabled
                ? "Users can access WHMCS-powered pages and API calls succeed."
                : "Server calls to WHMCS will be blocked until re-enabled."}
            </div>
          </div>
          <Switch checked={form.enabled} onCheckedChange={toggleEnabled} disabled={saveMut.isPending} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><PlugZap className="h-4 w-4" /> API configuration</CardTitle>
          <CardDescription>
            Configure your WHMCS API credentials. These override the backend secrets when set.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label className="text-xs">API URL</Label>
              <Input
                placeholder="https://billing.example.com/includes/api.php"
                value={form.api_url}
                onChange={(e) => setForm({ ...form, api_url: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">API Identifier</Label>
              <Input
                value={form.api_identifier}
                onChange={(e) => setForm({ ...form, api_identifier: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">API Secret</Label>
              <Input
                type="password"
                value={form.api_secret}
                onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Access Key (optional)</Label>
              <Input
                type="password"
                value={form.access_key}
                onChange={(e) => setForm({ ...form, access_key: e.target.value })}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => saveMut.mutate({
                api_url: form.api_url || undefined,
                api_identifier: form.api_identifier || undefined,
                api_secret: form.api_secret || undefined,
                access_key: form.access_key || undefined,
              })}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save configuration
            </Button>
            <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending}>
              {testMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
              Test connection
            </Button>
          </div>

          {result && (
            <div className={`rounded-md border p-3 text-sm ${result.ok ? "border-green-500/40 bg-green-500/5" : "border-destructive/40 bg-destructive/5"}`}>
              {result.ok ? (
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Connected — activity log entries: {result.totalResults}</div>
              ) : (
                <div className="flex items-center gap-2"><XCircle className="h-4 w-4 text-destructive" /> {result.error}</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---- Clients -------------------------------------------------------------
function ClientsTab() {
  const fn = useServerFn(listWhmcsClients);
  const [search, setSearch] = useState("");
  const [submitted, setSubmitted] = useState("");
  const q = useQuery({
    queryKey: ["whmcs-clients", submitted],
    queryFn: () => fn({ data: { search: submitted, limit: 50 } }),
  });

  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); setSubmitted(search); }}
      >
        <Input placeholder="Search email, name, company…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button type="submit">Search</Button>
        <Button type="button" variant="outline" onClick={() => q.refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </form>
      <DataState q={q} />
      {q.data && !q.data.notConfigured && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
              <TableHead>Company</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.clients.map((c: any) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-xs">{c.id}</TableCell>
                <TableCell>{c.firstname} {c.lastname}</TableCell>
                <TableCell>{c.email}</TableCell>
                <TableCell>{c.companyname || "—"}</TableCell>
                <TableCell><Badge variant={c.status === "Active" ? "default" : "secondary"}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---- Invoices ------------------------------------------------------------
function InvoicesTab() {
  const list = useServerFn(listWhmcsInvoices);
  const capture = useServerFn(captureWhmcsPayment);
  const create = useServerFn(createWhmcsInvoice);
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const q = useQuery({
    queryKey: ["whmcs-invoices", status],
    queryFn: () => list({ data: { status, limit: 50 } }),
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ userid: "", desc: "", amount: "" });

  const captureMut = useMutation({
    mutationFn: (id: number) => capture({ data: { invoiceId: id } }),
    onSuccess: () => { toast.success("Capture attempted"); qc.invalidateQueries({ queryKey: ["whmcs-invoices"] }); },
    onError: (e: any) => toast.error(e?.message),
  });

  const createMut = useMutation({
    mutationFn: () => create({ data: { userid: Number(form.userid), itemDescription: form.desc, itemAmount: Number(form.amount), sendInvoice: true } }),
    onSuccess: (r) => { toast.success(`Invoice #${r.invoiceId} created`); setOpen(false); setForm({ userid: "", desc: "", amount: "" }); qc.invalidateQueries({ queryKey: ["whmcs-invoices"] }); },
    onError: (e: any) => toast.error(e?.message),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">Status filter</Label>
          <select className="ml-2 rounded-md border bg-background px-2 py-1.5 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="Paid">Paid</option>
            <option value="Unpaid">Unpaid</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Refunded">Refunded</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
        <Button variant="outline" onClick={() => q.refetch()}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
        <Button onClick={() => setOpen((v) => !v)}>{open ? "Close" : "New invoice"}</Button>
      </div>

      {open && (
        <Card>
          <CardContent className="grid gap-3 p-4 md:grid-cols-4">
            <div><Label className="text-xs">Client ID</Label><Input value={form.userid} onChange={(e) => setForm({ ...form, userid: e.target.value })} /></div>
            <div className="md:col-span-2"><Label className="text-xs">Description</Label><Input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} /></div>
            <div><Label className="text-xs">Amount</Label><Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="md:col-span-4">
              <Button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.userid || !form.desc || !form.amount}>
                {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create & send
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <DataState q={q} />
      {q.data && !q.data.notConfigured && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead>
              <TableHead>Due</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.invoices.map((inv: any) => (
              <TableRow key={inv.id}>
                <TableCell className="font-mono text-xs">{inv.invoicenum || inv.id}</TableCell>
                <TableCell>{inv.firstname} {inv.lastname}</TableCell>
                <TableCell>{inv.date}</TableCell>
                <TableCell>{inv.duedate}</TableCell>
                <TableCell>{inv.total}</TableCell>
                <TableCell><Badge variant={inv.status === "Paid" ? "default" : inv.status === "Unpaid" ? "destructive" : "secondary"}>{inv.status}</Badge></TableCell>
                <TableCell>
                  {inv.status === "Unpaid" && (
                    <Button size="sm" variant="outline" onClick={() => captureMut.mutate(Number(inv.id))} disabled={captureMut.isPending}>
                      Capture
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---- Products ------------------------------------------------------------
function ProductsTab() {
  const fn = useServerFn(listWhmcsProducts);
  const q = useQuery({ queryKey: ["whmcs-products"], queryFn: () => fn({}) });
  return (
    <div className="space-y-3">
      <Button variant="outline" onClick={() => q.refetch()}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
      <DataState q={q} />
      {q.data && !q.data.notConfigured && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Group</TableHead>
              <TableHead>Type</TableHead><TableHead>Monthly</TableHead><TableHead>Annual</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.products.map((p: any) => (
              <TableRow key={p.pid}>
                <TableCell className="font-mono text-xs">{p.pid}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.groupname}</TableCell>
                <TableCell>{p.type}</TableCell>
                <TableCell>{p.pricing?.USD?.monthly ?? "—"}</TableCell>
                <TableCell>{p.pricing?.USD?.annually ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---- Orders --------------------------------------------------------------
function OrdersTab() {
  const fn = useServerFn(listWhmcsOrders);
  const [status, setStatus] = useState("");
  const q = useQuery({
    queryKey: ["whmcs-orders", status],
    queryFn: () => fn({ data: { status, limit: 50 } }),
  });
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <select className="rounded-md border bg-background px-2 py-1.5 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          <option value="Pending">Pending</option>
          <option value="Active">Active</option>
          <option value="Fraud">Fraud</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        <Button variant="outline" onClick={() => q.refetch()}><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <DataState q={q} />
      {q.data && !q.data.notConfigured && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead>
              <TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.orders.map((o: any) => (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-xs">{o.ordernum || o.id}</TableCell>
                <TableCell>{o.firstname} {o.lastname}</TableCell>
                <TableCell>{o.date}</TableCell>
                <TableCell>{o.amount}</TableCell>
                <TableCell>{o.paymentmethod}</TableCell>
                <TableCell><Badge>{o.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---- Logs ----------------------------------------------------------------
function LogsTab() {
  const fn = useServerFn(listWhmcsLogs);
  const q = useQuery({ queryKey: ["whmcs-logs"], queryFn: () => fn({}), refetchInterval: 5000 });
  return (
    <div className="space-y-3">
      <Button variant="outline" onClick={() => q.refetch()}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
      <DataState q={q} />
      {q.data && !(q.data as any).notConfigured && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>Status</TableHead>
              <TableHead>HTTP</TableHead><TableHead>Duration</TableHead><TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.logs.map((l: any) => (
              <TableRow key={l.id}>
                <TableCell className="text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                <TableCell className="font-mono text-xs">{l.action}</TableCell>
                <TableCell><Badge variant={l.status === "success" ? "default" : "destructive"}>{l.status}</Badge></TableCell>
                <TableCell>{l.http_status ?? "—"}</TableCell>
                <TableCell>{l.duration_ms}ms</TableCell>
                <TableCell className="max-w-[300px] truncate text-xs text-destructive">{l.error}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---- Shared --------------------------------------------------------------
function DataState({ q }: { q: { isPending: boolean; error: unknown; data?: any } }) {
  if (q.isPending) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (q.error) return <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{(q.error as Error).message}</div>;
  if (q.data?.notConfigured) {
    return (
      <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div>
          <div className="font-medium">WHMCS is not connected yet</div>
          <div className="text-muted-foreground">
            {q.data.message ?? "Add your WHMCS API URL, identifier and secret in the Connection tab to start pulling data."}
          </div>
        </div>
      </div>
    );
  }
  return null;
}

